/**
 * Backend error rate (UDC/Errors namespace).
 *
 * Backend shape:
 *   {
 *     status: 'ok' | 'unavailable',
 *     error: string | null,
 *     last_1h: number,
 *     last_24h: number,
 *     top_error_codes_24h: Array<{ error_code: string, count: number }>,
 *   }
 *
 * 2026-08-21 (U29): these two counters were structurally 0 until the backend
 * stopped querying the empty dimension set. UDC/Errors ErrorCount is stamped
 * {ErrorCode, Environment} and nothing publishes it dimensionless, so the
 * card read 0 through a 14-error day. The per-code breakdown below is no
 * longer deferred — it falls out of the same enumerate-and-sum.
 */
import { formatNumber } from '@/lib/utils';
import { TileMeta, TileUnavailable, isUnavailable, tileShell } from './TileStatus';

interface Data extends TileMeta {
  last_1h: number;
  last_24h: number;
  top_error_codes_24h?: Array<{ error_code: string; count: number }>;
}

export function ErrorRateCard({ data }: { data: Data }) {
  const unavailable = isUnavailable(data);
  const topCodes = data?.top_error_codes_24h ?? [];
  return (
    <section className={`border rounded-lg p-6 ${tileShell(unavailable, 'bg-card')}`}>
      <h3 className="text-lg font-semibold mb-3">Backend error rate</h3>
      {unavailable && <TileUnavailable error={data?.error} />}
      <div className="flex gap-8 mb-3">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Last 1h</div>
          <div className="text-2xl font-semibold">
            {unavailable ? '—' : formatNumber(data?.last_1h ?? 0)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Last 24h</div>
          <div className="text-2xl font-semibold">
            {unavailable ? '—' : formatNumber(data?.last_24h ?? 0)}
          </div>
        </div>
      </div>
      {!unavailable && topCodes.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Top error codes (24h)
          </div>
          <ul className="text-sm space-y-1">
            {topCodes.map((e) => (
              <li key={e.error_code} className="font-mono">
                <span className="text-foreground">{e.error_code}</span>
                <span className="text-muted-foreground"> × {formatNumber(e.count)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!unavailable && topCodes.length === 0 && (
        <p className="text-xs text-muted-foreground">No errors recorded in the last 24h.</p>
      )}
    </section>
  );
}
